import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
  OverviewChartTimeframe,
  OverviewChartType,
  SortingDirection,
  SortingToggleOptions,
} from '../../screens/Overview/types';

export type OverviewSubSort =
  | 'Total Value'
  | 'Total Stable Profit'
  | 'Total Asset Profit'
  | 'Current Stable Profit'
  | 'Current Asset Profit'
  | 'Non-Algo Diff';

export type OverviewChartScale = 'lin' | 'log';

interface UIState {
  overviewChartTimeframe: OverviewChartTimeframe;
  overviewChartType: OverviewChartType;
  overviewChartScale: OverviewChartScale;
  overviewChartPercentage: boolean;
  sorting: SortingToggleOptions;
  sortingDirection: SortingDirection;
  overviewSubSort: OverviewSubSort;
  sideMenuCollapsed: boolean;
  overviewValuesHidden: boolean;
  overviewSubView: 'grid' | 'list';
}

const initialState: UIState = {
  overviewChartTimeframe: OverviewChartTimeframe.All,
  overviewChartType: OverviewChartType.ProfitOnly,
  overviewChartScale: 'lin',
  overviewChartPercentage: true,
  sorting: SortingToggleOptions.Diversity,
  sortingDirection: SortingDirection.Descending,
  overviewSubSort: 'Total Value',
  sideMenuCollapsed: false,
  overviewValuesHidden: false,
  overviewSubView: 'grid',
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setSorting: (state, action: PayloadAction<SortingToggleOptions>) => {
      state.sorting = action.payload;
    },
    setOverviewChartPercentage: (state, action: PayloadAction<boolean>) => {
      state.overviewChartPercentage = action.payload;
    },
    setOverviewChartTimeframe: (
      state,
      action: PayloadAction<OverviewChartTimeframe>,
    ) => {
      state.overviewChartTimeframe = action.payload;
    },
    setOverviewChartType: (state, action: PayloadAction<OverviewChartType>) => {
      state.overviewChartType = action.payload;
    },
    setOverviewChartScale: (
      state,
      action: PayloadAction<OverviewChartScale>,
    ) => {
      state.overviewChartScale = action.payload;
    },
    setSortingDirection: (state, action: PayloadAction<SortingDirection>) => {
      state.sortingDirection = action.payload;
    },
    setOverviewSubSort: (state, action: PayloadAction<OverviewSubSort>) => {
      state.overviewSubSort = action.payload;
    },
    setSideMenuCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sideMenuCollapsed = action.payload;
    },
    toggleSideMenuCollapsed: (state) => {
      state.sideMenuCollapsed = !state.sideMenuCollapsed;
    },
    setOverviewValuesHidden: (state, action: PayloadAction<boolean>) => {
      state.overviewValuesHidden = action.payload;
    },
    toggleOverviewValuesHidden: (state) => {
      state.overviewValuesHidden = !state.overviewValuesHidden;
    },
    setOverviewSubView: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.overviewSubView = action.payload;
    },
  },
});

export const {
  setSorting,
  setSortingDirection,
  setOverviewChartTimeframe,
  setOverviewChartType,
  setOverviewChartScale,
  setOverviewChartPercentage,
  setOverviewSubSort,
  setSideMenuCollapsed,
  toggleSideMenuCollapsed,
  setOverviewValuesHidden,
  toggleOverviewValuesHidden,
  setOverviewSubView,
} = uiSlice.actions;

export const selectOverviewChartTimeframe = (state: RootState) =>
  state.ui.overviewChartTimeframe;
export const selectSorting = (state: RootState) => state.ui.sorting;
export const selectSortingDirection = (state: RootState) =>
  state.ui.sortingDirection;
export const selectOverviewChartPercentage = (state: RootState) =>
  state.ui.overviewChartPercentage;
export const selectOverviewChartType = (state: RootState) =>
  state.ui.overviewChartType;
export const selectOverviewChartScale = (state: RootState) =>
  state.ui.overviewChartScale ?? 'lin';
export const selectOverviewSubSort = (state: RootState) =>
  state.ui.overviewSubSort;
export const selectSideMenuCollapsed = (state: RootState) =>
  state.ui.sideMenuCollapsed;
export const selectOverviewValuesHidden = (state: RootState) =>
  state.ui.overviewValuesHidden;
export const selectOverviewSubView = (state: RootState) =>
  state.ui.overviewSubView;

export default uiSlice.reducer;
